/**
 * Constants for coaching surface and related functionality
 */

// Default message when no coaching content is available
export const NO_COACHING_MESSAGE = "No coaching guidance at this time."

// ── The "opening doors" coaching spine ──────────────────────────────────────
// One metaphor carries the whole experience: the trainee's job is to open doors
// to getting help, and PACE is the simple structure that opens them. Authored
// once here for clinical review and live reuse; the replay seed copies the same
// strings into its paginated coach_dialogue_page cards.

export type CoachingFocus = "symptoms" | "advocate" | "diagnostic"

// Canonical plain-language PACE gloss — reuse this exact wording everywhere PACE
// is introduced so the language never drifts.
export const PACE_GLOSS = {
  presenting: "Presenting — describing what's been going on",
  asking: "Asking — asking questions",
  checking: "Checking — checking you've understood each other",
  expressing: "Expressing — saying your concerns and what you want",
} as const

// The focus-tailored framings, paginated (one bite-sized card per array element,
// Duolingo / Chess.com style). Each names all four PACE steps in full, then
// tilts emphasis to the chosen focus.
export const OPENING_DOORS_FRAMINGS: Record<CoachingFocus, string[]> = {
  symptoms: [
    "Sounds good! Your job in this GP visit is to share the information your GP uses to open doors to getting you help.",
    "To focus on what they need, remember PACE — Presenting information · Asking questions.",
    "…Checking understanding · Expressing concerns.",
    "Follow that structure — and remember, the job is just to open doors to further help. It's simple!",
  ],
  advocate: [
    "Great choice. Advocating means making sure your experience actually lands — so the doors that open are the right ones for you.",
    "You'll still use all of PACE: Presenting what's going on · Asking questions · Checking the doctor has understood you · Expressing what you want.",
    "Because you already know your history, two carry the most weight here — Checking and Expressing. Tell Dr Okafor early that you've been through this before and what you're hoping to get from today.",
    "That lets them tailor everything that follows. You're still opening doors — you're just steering which ones.",
  ],
  diagnostic: [
    "Good — understanding the why behind what the doctor does is its own kind of advocacy.",
    "You'll still use all of PACE: Presenting what's going on · Asking questions · Checking understanding · Expressing concerns.",
    "Here the two that matter most are Asking and Checking. When the doctor suggests something, ask what it's for and what happens next — then reflect it back to be sure you've got it.",
    "Every question you ask opens a door to a clearer answer. Keep opening them until the process makes sense to you.",
  ],
}

// ── The four coaching "moves" — the shared spine of the experience ───────────
// We dropped the PACE acronym in favour of four plain-language behaviours. This
// is the single source of truth: the pre-conversation intro teaches them (one
// card each in intro.yml) and the in-conversation "Tap for Help" offers the same
// four — so the vocabulary a trainee learns up front is the vocabulary they're
// nudged with live. Keep the `title` strings byte-aligned with the intro.yml
// card titles.
export type CoachingMoveId = "describe" | "ask" | "check" | "say"

export interface CoachingMove {
  id: CoachingMoveId
  // Gerund form — the trainee-facing name of the behaviour ("the coaching layer").
  label: string
  // Imperative form — used as the teaching card's title in the intro.
  title: string
  // A one-line nudge for the live "Tap for Help" affordance.
  help: string
}

export const COACHING_MOVES: CoachingMove[] = [
  {
    id: "describe",
    label: "Describing how you feel",
    title: "Describe how you feel",
    help: "Say what's actually been going on, in your own words — the real version.",
  },
  {
    id: "ask",
    label: "Asking what you need",
    title: "Ask what you need",
    help: "Ask a real question — it's how you find out what help is available.",
  },
  {
    id: "check",
    label: "Checking you understood",
    title: "Check you understood",
    help: "If something's unclear — a word, a next step, who finds out — say so.",
  },
  {
    id: "say",
    label: "Saying what you want",
    title: "Say what you want",
    help: "Say what you'd like to happen next, even tentatively.",
  },
]

// Focus-aware throughout-note templates (one short card each). Placeholders are
// filled in per moment; the door metaphor stays constant.
export const THROUGHOUT_NOTE_STYLES = {
  brief:
    'That "{line}" just closed a door before the doctor could reach it — {focusVerb} instead.',
  interrupt:
    "Hold on — you're about to leave the most important door shut. {nameTheMove}. This is your life: say what you want.",
} as const

// Event type names for coaching-related events
export const COACHING_EVENT_TYPES = {
  DIALOGUE_PAGE: "coach_dialogue_page",
  PROMPT_SELECTED: "coaching_prompt_selected",
  ACKNOWLEDGED: "coaching_acknowledged",
  SKIPPED: "coaching_skipped",
  SURFACE_TOGGLED: "coaching_surface_toggled",
  FOCUS_CHANGED: "surface_focus_changed",
} as const

// Color mappings for timeline markers
export const COACHING_TIMELINE_COLORS = {
  coach_dialogue_page: "bg-purple-600",
  coaching_prompt_selected: "bg-purple-500",
  coaching_acknowledged: "bg-purple-400",
  coaching_skipped: "bg-purple-300",
  surface_focus_changed: "bg-amber-500",
  coaching_surface_toggled: "bg-amber-400",
} as const

// Default initial coaching message structure
export const DEFAULT_COACHING_WELCOME = {
  page_type: "goal_selection",
  page_number: 1,
  total_pages: 1,
  content: {
    title: "Welcome to OurHerd Conversations!",
    instructions: ["Choose a focus to get started."],
  },
} as const
